import { Data2, useDataTypesHooks, vatTaxCodeFromYield } from "../../../features/data-types"
import { useServer } from "../../../services/server"
import { TableName } from "../../../tables"
import { NoteGenerationType } from "../../deliveries/components/types"
import { FiscalDocument, FiscalDocumentStatus, FiscalDocumentType, getActiveInvoices, hasSentInvoice, isUninvoicedFiscalDocumentStatus, useFiscalDocumentBuilders, useFiscalDocumentLoaders } from "../../fiscal-documents"
import { Item, usePriceOutlineHooks, usePricesHooks } from "../../operations"
import { useProductLoaders } from "../../products"
import { HoardOrder, HoardType, SaleOperation } from "../../sales"
import { useUnitTypeLoaders } from "../../stock"
import { useAdjustmentNoteActions } from "../actions/adjustmentNoteActions"
import { useAdjustmentNoteBuilders } from "../builders/adjustmentNoteBuilders"
import { useAdjustmentNoteLoaders } from "../loaders/adjustmentNoteLoaders"
import { round } from "../../../utils/typeUtils"
import { AdjustmentNote, AdjustmentNoteType } from "../state/types"
import {
    correctManualAdjustmentPrices,
    getManualAdjustmentAmount,
    getManualCreditNoteMaxAmount,
    getNetUnitTotal,
    isManualCreditNote,
    ManualCreditNoteBalances,
    ManualCreditNoteData,
    ManualCreditNoteLine,
    ManualCreditNoteMode,
    ManualCreditNoteRequest,
    ManualCreditNoteResult,
    prorateManualAdjustment
} from "../utils/manualCreditNoteUtils"

const AMOUNT_TOLERANCE = 0.01

export const useAdjustmentNoteService = () => {
    const { buildAdjustmentNote } = useAdjustmentNoteBuilders()
    const { buildFiscalDocumentItem } = useFiscalDocumentBuilders()
    
    const { generateCode } = useDataTypesHooks()
    const { getPriceOutline, getPriceOutlineLive, getItemPriceOutline } = usePriceOutlineHooks()
    const pricesHooks = usePricesHooks()

    const productLoaders = useProductLoaders()
    const unitTypeLoaders = useUnitTypeLoaders()
    const fiscalDocumentLoaders = useFiscalDocumentLoaders()
    const adjustmentNoteLoaders = useAdjustmentNoteLoaders()

    const { generateCreditNote, createManualCreditNote: createServerManualCreditNote } = useServer()
    const { saveAdjustmentNote } = useAdjustmentNoteActions()

    // A note can only be sent to the fiscal authority when the operation was invoiced.
    // An invoice preview (or a rejected one) isn't an invoice: a note created then is discounted by the invoice
    const getFiscalStatus = async (
        operationId: string,
        fiscalStatus?: FiscalDocumentStatus
    ) => {
        const { loadOperationFiscalDocuments } = fiscalDocumentLoaders()
        const fiscalDocuments = await loadOperationFiscalDocuments(operationId, false)
        return hasSentInvoice(fiscalDocuments) ? fiscalStatus : FiscalDocumentStatus.NOT_APPLICABLE
    }

    // A returned or cancelled unit credits its price minus the manual discounts it already got: otherwise a discount
    // plus the return of everything would credit more than what was sold
    const getNetOutlineItems = async (
        table: TableName,
        operation: SaleOperation,
        operationItems: Item[],
        paymentTypeYield: number
    ) => {
        const { loadOperationAdjustmentNotes } = adjustmentNoteLoaders()
        const adjustmentNotes = await loadOperationAdjustmentNotes(operation.id, false)
        const legacyFactor = getLegacyManualDiscountFactor(table, operation, adjustmentNotes)
        return getPriceOutlineLive(operationItems, undefined, paymentTypeYield).items.map((item, index) => {
            const netUnitTotal = getNetUnitTotal(item.unitTotal, operationItems[index].manualUnitDiscount, legacyFactor)
            const factor = item.unitTotal > 0 ? netUnitTotal / item.unitTotal : 0
            return {
                ...item,
                unitSubtotal: item.unitSubtotal * factor,
                unitTotal: netUnitTotal,
                totalWithoutVAT: item.totalWithoutVAT * factor,
                vatAmount: item.vatAmount * factor
            }
        })
    }

    const createAdjustmentNote = async (
        adjustmentNoteData: Data2,
        itemsData: Data2[],
        operation: SaleOperation,
        noteType: NoteGenerationType
    ) => {
        const code = await generateCode(TableName.ADJUSTMENT_NOTES)
        
        const items = itemsData
            .map(itemData => ({
                itemId: itemData.itemId! as string,
                quantity: itemData.quantity! as number,
            }))
            .filter(item => item.quantity > 0)

        const operationItems: Item[] = operation.items
            .filter(item => items.some(itemData => itemData.itemId === item.id))
            .map(item => ({
                ...item,
                quantity: items.find(itemData => itemData.itemId === item.id)!.quantity
            }))

        const table = adjustmentNoteData.table as TableName

        const { loadProducts } = productLoaders()
        const loadedProducts = await loadProducts(operationItems.map(item => item.productId))

        // En un acopio financiero el precio de lista ya trae IVA, así que cada línea se descompone
        // en lugar de aplicarle IVA encima (nunca se usa el outline de ítems, que lo duplicaría).
        const isFinancialHoard = table === TableName.HOARD_ORDERS && (operation as HoardOrder).type === HoardType.FINANCIAL
        const paymentTypeYield = operation.paymentTypeYield || 0
        const outlineItems = isFinancialHoard
            ? operationItems.map(item => {
                const vatTaxYield = item.vatTaxYield ?? pricesHooks.product.vatTaxYield(item.productId)
                const price = (operation as HoardOrder).pricesList?.[item.productId] ?? item.price
                const unitSubtotal = price / (1 + vatTaxYield / 100) * (1 + paymentTypeYield / 100)
                const unitTotal = unitSubtotal * (1 + vatTaxYield / 100)
                const quantity = item.quantity
                return {
                    productId: item.productId,
                    quantity,
                    unitSubtotal,
                    unitTotal,
                    totalWithoutVAT: unitSubtotal * quantity,
                    vatAmount: (unitTotal - unitSubtotal) * quantity
                }
            })
            : await getNetOutlineItems(table, operation, operationItems, paymentTypeYield)

        // Perceptions apply proportionally to the note base amount (not the whole operation perceptions)
        const noteBaseAmount = outlineItems.reduce((sum, item) => sum + item.totalWithoutVAT, 0)
        const perceptions = (operation.perceptions || []).map(perception => ({
            ...perception,
            baseAmount: round(noteBaseAmount),
            amount: perception.baseAmount > 0 ? round(perception.amount * noteBaseAmount / perception.baseAmount) : 0
        }))
        const perceptionsAmount = perceptions.reduce((sum, perception) => sum + perception.amount, 0)

        const adjustmentNoteItems = outlineItems.map((item, index) => {
            const product = loadedProducts.find(product => product.id === item.productId)!
            const unitType = unitTypeLoaders().findUnitType(product.unitTypeId)!
            // El outline conserva el orden de los ítems de la operación: el itemId no se pierde
            // aunque haya dos ítems del mismo producto en lotes distintos.
            const operationItem = operationItems[index]
            // En un acopio financiero el IVA vive en el acopio, no en el ítem: si falta en el ítem,
            // se toma del precio del producto (misma fuente que usa el desglose del outline).
            const vatTaxYield = operationItem.vatTaxYield ?? pricesHooks.product.vatTaxYield(operationItem.productId)
            return {
                ...buildFiscalDocumentItem({
                    name: product.name,
                    unitTypeName: unitType.name,
                    vatTaxCode: vatTaxCodeFromYield(vatTaxYield),
                    vatTaxYield,
                    unitSubtotal: item.unitSubtotal,
                    unitTotal: item.unitTotal,
                    quantity: item.quantity,
                    totalWithoutVAT: item.totalWithoutVAT,
                    vatAmount: item.vatAmount
                }),
                itemId: operationItem.id
            }
        })

        const fiscalStatus = await getFiscalStatus(operation.id, adjustmentNoteData.fiscalStatus as FiscalDocumentStatus)

        const adjustmentNote = buildAdjustmentNote({
            ...adjustmentNoteData,
            code,
            items: adjustmentNoteItems,
            amount: round(outlineItems.reduce((sum, item) => sum + item.unitTotal * item.quantity, 0) + perceptionsAmount),
            fiscalStatus
        })
        const savedAdjustmentNote = await saveAdjustmentNote(adjustmentNote)
        
        if (noteType === NoteGenerationType.CREDIT_NOTE) {
            await generateCreditNote(savedAdjustmentNote.id)
        }

        return savedAdjustmentNote
    }

    // What is already invoiced and can still be credited by a fiscal note: the active sent invoices minus their
    // credit notes (without perceptions) and minus the fiscal notes still waiting to be emitted
    const getCreditableInvoicedAmount = (
        fiscalDocuments: FiscalDocument[],
        adjustmentNotes: AdjustmentNote[]
    ) => {
        const isSent = (fiscalDocument: FiscalDocument) => !isUninvoicedFiscalDocumentStatus(fiscalDocument.status)
        const amountWithoutPerceptions = (fiscalDocument: FiscalDocument) =>
            (fiscalDocument.details?.totals.total || 0) - (fiscalDocument.details?.totals.totalPerceptions || 0)
        const invoiced = getActiveInvoices(fiscalDocuments)
            .filter(isSent)
            .reduce((sum, invoice) => sum + amountWithoutPerceptions(invoice), 0)
        const credited = fiscalDocuments
            .filter(fiscalDocument => fiscalDocument.documentType === FiscalDocumentType.CREDIT_NOTE && isSent(fiscalDocument) && !fiscalDocument.annulmentOfFiscalDocumentId)
            .reduce((sum, creditNote) => sum + amountWithoutPerceptions(creditNote), 0)
        const pendingNotes = adjustmentNotes
            .filter(adjustmentNote => adjustmentNote.type === AdjustmentNoteType.CREDIT && adjustmentNote.fiscalStatus === FiscalDocumentStatus.PENDING && !adjustmentNote.fiscalDocumentId)
            .reduce((sum, adjustmentNote) => sum + adjustmentNote.amount, 0)
        return Math.max(0, round(invoiced - credited - pendingNotes))
    }

    const isFinancialHoardOperation = (table: TableName, operation: SaleOperation) =>
        table === TableName.HOARD_ORDERS && (operation as HoardOrder).type === HoardType.FINANCIAL

    // The manual notes created before they were per item have no item: their discount is taken as prorated over the
    // whole operation, as the share of the operation total (without perceptions) they credited
    const getLegacyManualDiscountFactor = (
        table: TableName,
        operation: SaleOperation,
        adjustmentNotes: AdjustmentNote[]
    ): number => {
        const legacyAmount = adjustmentNotes
            .filter(adjustmentNote => isManualCreditNote(adjustmentNote) && adjustmentNote.items.every(item => !item.itemId))
            .reduce((sum, adjustmentNote) => sum + adjustmentNote.amount, 0)
        if (legacyAmount <= 0) return 0
        const priceOutline = getPriceOutline(table, operation)
        const total = priceOutline.total - priceOutline.perceptionsAmount
        return total > 0 ? Math.min(1, legacyAmount / total) : 0
    }

    // What is invoiced and can still be credited, and what is left to invoice (adjusted total minus what is invoiced,
    // both without perceptions)
    const getBalances = (
        table: TableName,
        operation: SaleOperation,
        fiscalDocuments: FiscalDocument[],
        adjustmentNotes: AdjustmentNote[]
    ): ManualCreditNoteBalances => {
        const creditableAmount = getCreditableInvoicedAmount(fiscalDocuments, adjustmentNotes)
        const priceOutline = getPriceOutline(table, operation)
        const adjustedTotal = priceOutline.total - priceOutline.perceptionsAmount - (operation.adjustedAmount || 0)
        return {
            creditableAmount,
            remainingAmount: Math.max(0, round(adjustedTotal - creditableAmount))
        }
    }

    // The items a manual credit note can discount, at the price the customer sees (with VAT and discounts, without the
    // payment type surcharge) net of the manual discounts they already got. A sale is invoiced all at once
    const getManualCreditNoteLines = (
        table: TableName,
        operation: SaleOperation,
        fiscalDocuments: FiscalDocument[],
        adjustmentNotes: AdjustmentNote[]
    ): ManualCreditNoteLine[] => {
        const surchargeFactor = 1 + (operation.paymentTypeYield || 0) / 100
        const legacyFactor = getLegacyManualDiscountFactor(table, operation, adjustmentNotes)
        const saleInvoiced = table === TableName.SALE_ORDERS && hasSentInvoice(fiscalDocuments)
        return operation.items.map(item => {
            const activeQuantity = Math.max(0, item.quantity - (item.cancelledQuantity || 0))
            const { unitTotal } = getItemPriceOutline(item)
            return {
                itemId: item.id,
                activeQuantity,
                invoicedQuantity: saleInvoiced ? activeQuantity : Math.min(activeQuantity, item.invoiceStats?.invoiced || 0),
                unitPrice: getNetUnitTotal(unitTotal, (item.manualUnitDiscount || 0) / surchargeFactor, legacyFactor),
                vatTaxYield: item.vatTaxYield ?? pricesHooks.product.vatTaxYield(item.productId)
            }
        })
    }

    // Everything the manual credit note form needs, read from the db
    const loadManualCreditNoteData = async (
        table: TableName,
        operation: SaleOperation
    ): Promise<ManualCreditNoteData> => {
        const { loadOperationFiscalDocuments } = fiscalDocumentLoaders()
        const { loadOperationAdjustmentNotes } = adjustmentNoteLoaders()
        const [fiscalDocuments, adjustmentNotes] = await Promise.all([
            loadOperationFiscalDocuments(operation.id, false),
            loadOperationAdjustmentNotes(operation.id, false)
        ])
        return {
            balances: getBalances(table, operation, fiscalDocuments, adjustmentNotes),
            lines: isFinancialHoardOperation(table, operation) ? [] : getManualCreditNoteLines(table, operation, fiscalDocuments, adjustmentNotes),
            surchargeFactor: 1 + (operation.paymentTypeYield || 0) / 100
        }
    }

    // Manual credit note. What to discount is decided here, because it needs the price engine: per item in a sale or a
    // product hoard (prorated or on the corrected products), or an amount in a financial hoard (it has no items yet).
    // The server splits it in a fiscal note on what is already invoiced (emitted from the documents panel) and an
    // internal one on what is left to invoice, and saves the notes with the operation adjustment, its payment, the
    // discount per unit of the items and the customer credit in favor in a single transaction
    const createManualCreditNote = async (
        table: TableName,
        operation: SaleOperation,
        request: ManualCreditNoteRequest,
        reason: string
    ): Promise<ManualCreditNoteResult> => {
        const priceOutline = getPriceOutline(table, operation)
        const body = {
            reason,
            expectedAdjustedAmount: operation.adjustedAmount || 0,
            operationTotal: priceOutline.total,
            perceptionsAmount: priceOutline.perceptionsAmount
        }

        if (isFinancialHoardOperation(table, operation)) {
            return createServerManualCreditNote(table, operation.id, { ...body, amount: request.amount || 0 })
        }

        const { lines, surchargeFactor } = await loadManualCreditNoteData(table, operation)
        const parts = request.mode === ManualCreditNoteMode.PRODUCTS ?
            correctManualAdjustmentPrices(new Map(Object.entries(request.correctPrices || {})), lines, surchargeFactor) :
            prorateManualAdjustment(request.amount || 0, lines, surchargeFactor)
        if (parts.length === 0) {
            throw new Error('La nota de crédito no tiene nada para descontar.')
        }
        if (getManualAdjustmentAmount(parts) > getManualCreditNoteMaxAmount(lines, surchargeFactor) + AMOUNT_TOLERANCE) {
            throw new Error('La nota de crédito supera lo que queda de la operación.')
        }

        return createServerManualCreditNote(table, operation.id, { ...body, parts })
    }

    // Internal credit note (never sent to the fiscal authority) that documents the amount received above what was owed,
    // which was credited to the customer credit in favor. It doesn't change the operation adjusted amount.
    const createOverpaymentNote = async (
        table: TableName,
        operationId: string,
        amount: number,
        date: Date
    ) => {
        const code = await generateCode(TableName.ADJUSTMENT_NOTES)
        const item = buildFiscalDocumentItem({
            name: 'Pago excedente - Crédito a favor',
            unitTypeName: '-',
            vatTaxYield: 0,
            unitSubtotal: amount,
            unitTotal: amount,
            quantity: 1,
            totalWithoutVAT: amount,
            vatAmount: 0
        })

        const adjustmentNote = buildAdjustmentNote({
            code,
            type: AdjustmentNoteType.CREDIT,
            table,
            operationId,
            items: [item],
            amount,
            date,
            fiscalStatus: FiscalDocumentStatus.NOT_APPLICABLE
        })
        return saveAdjustmentNote(adjustmentNote)
    }

    // Nota de débito por un monto (ej. ampliación de un acopio financiero ya facturado).
    // Queda pendiente de emitir: el servidor todavía no tiene endpoint para emitir notas de débito.
    const createDebitNote = async (
        table: TableName,
        operationId: string,
        itemName: string,
        amount: number,
        vatTaxYield = 0
    ) => {
        const code = await generateCode(TableName.ADJUSTMENT_NOTES)
        const totalWithoutVAT = amount / (1 + vatTaxYield / 100)
        const vatAmount = amount - totalWithoutVAT

        const adjustmentNote = buildAdjustmentNote({
            code,
            type: AdjustmentNoteType.DEBIT,
            table,
            operationId,
            items: [buildFiscalDocumentItem({
                name: itemName,
                unitTypeName: 'Unidad',
                vatTaxCode: vatTaxCodeFromYield(vatTaxYield),
                vatTaxYield,
                unitSubtotal: totalWithoutVAT,
                unitTotal: amount,
                quantity: 1,
                totalWithoutVAT,
                vatAmount
            })],
            amount,
            fiscalStatus: FiscalDocumentStatus.PENDING
        })
        return saveAdjustmentNote(adjustmentNote)
    }

    // Internal credit note (never sent to the fiscal authority) for the credit left unused when closing a financial hoard.
    // Unlike the overpayment note, it does lower what the customer owes (it is applied as an adjustment note).
    const createUnusedHoardCreditNote = async (
        hoardId: string,
        amount: number
    ) => {
        const code = await generateCode(TableName.ADJUSTMENT_NOTES)
        const item = buildFiscalDocumentItem({
            name: 'Crédito no utilizado - Cierre de acopio',
            unitTypeName: '-',
            vatTaxYield: 0,
            unitSubtotal: amount,
            unitTotal: amount,
            quantity: 1,
            totalWithoutVAT: amount,
            vatAmount: 0
        })

        const adjustmentNote = buildAdjustmentNote({
            code,
            type: AdjustmentNoteType.CREDIT,
            table: TableName.HOARD_ORDERS,
            operationId: hoardId,
            items: [item],
            amount,
            reason: 'Cierre de acopio con crédito pendiente',
            fiscalStatus: FiscalDocumentStatus.NOT_APPLICABLE
        })
        return saveAdjustmentNote(adjustmentNote)
    }

    return {
        createAdjustmentNote,
        loadManualCreditNoteData,
        createManualCreditNote,
        createOverpaymentNote,
        createDebitNote,
        createUnusedHoardCreditNote
    }
}
